<script setup>
// =============================================================================
// FangateContentToolbar — the row above the product list
//
// Layout: [Folder ▾] [search …] [filter ⚙] [sort ⇅] [refresh ↻]
//
// Owns its own dropdown open-states (folder/filter/sort menus) and an
// outside-click listener that closes them all. The parent's product-card
// link-selector menu is separate and not affected by this listener.
//
// State contract:
//   v-model:search       (string)
//   v-model:folder       (string|null)
//   v-model:sort         (string — id of one of SORT_OPTIONS)
//   :active-types        (Array<string>)  read-only snapshot of activeFilters.type
//   :folders             (Array<string>)
//   :filter-count        (number)
//   :is-loading-products (boolean)         disables refresh while a fetch runs
//
// Emits:
//   update:search / update:folder / update:sort
//   toggle-type(value)   parent flips the Set
//   clear-filters
//   refresh
// =============================================================================

import { ref, onMounted, onUnmounted } from 'vue';

defineProps({
  search:             { type: String,  default: '' },
  folder:             { type: [String, null], default: null },
  sort:               { type: String,  default: 'newest' },
  activeTypes:        { type: Array,   default: () => [] },
  folders:            { type: Array,   default: () => [] },
  filterCount:        { type: Number,  default: 0 },
  isLoadingProducts:  { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:search',
  'update:folder',
  'update:sort',
  'toggle-type',
  'clear-filters',
  'refresh',
]);

// Internal — only this toolbar owns these. Outside-click closes them.
const folderOpen = ref(false);
const filterOpen = ref(false);
const sortOpen   = ref(false);

const closeAllMenus = () => {
  folderOpen.value = false;
  filterOpen.value = false;
  sortOpen.value   = false;
};

onMounted(() => document.addEventListener('click', closeAllMenus));
onUnmounted(() => document.removeEventListener('click', closeAllMenus));

// Type filter options. The `value` is what flows in/out via activeTypes
// + toggle-type — it matches Fangate's API convention (lowercase singular
// — `image` / `video`). The `label` is what we render in the dropdown.
// Side-panel mock data and the upload modal both normalize to these
// values so the filter matches on real and mock data alike.
const TYPE_OPTIONS = [
  { value: 'image', label: 'Photos' },
  { value: 'video', label: 'Video'  },
];

// Sort dropdown options. `label` is shown in the dropdown row;
// `short` is reserved for a compact pill display (not currently used —
// the sort button is icon-only with a tooltip).
const SORT_OPTIONS = [
  { id: 'newest',     label: 'Newest first',       short: 'Newest'  },
  { id: 'oldest',     label: 'Oldest first',       short: 'Oldest'  },
  { id: 'title-asc',  label: 'Title A → Z',        short: 'A–Z'     },
  { id: 'price-desc', label: 'Price: high → low',  short: 'Price ↓' },
  { id: 'price-asc',  label: 'Price: low → high',  short: 'Price ↑' },
];

const onSearchInput   = e => emit('update:search', e.target.value);
// Fangate's /content-folders returns objects { id, name, ... }. Earlier
// callers passed plain strings. Accept both so the toolbar doesn't render
// `{ "id": 46, "name": "Test" }` as text when the parent forgets to map
// down to names. Same defensive extraction below in pickFolder so the
// emit always carries a string the parent code already handles.
const folderName = f => {
  if (f == null) return '';
  if (typeof f === 'string') return f;
  return f.name || f.label || '';
};
const pickFolder = f => {
  emit('update:folder', folderName(f) || f);
  folderOpen.value = false;
};
const pickSort = id => { emit('update:sort', id); sortOpen.value = false; };

// Toggling one dropdown closes the other two — single-open invariant.
const toggleFolderMenu = () => { const v = !folderOpen.value; closeAllMenus(); folderOpen.value = v; };
const toggleFilterMenu = () => { const v = !filterOpen.value; closeAllMenus(); filterOpen.value = v; };
const toggleSortMenu   = () => { const v = !sortOpen.value;   closeAllMenus(); sortOpen.value   = v; };
</script>

<template>
  <div class="fp-toolbar">
    <!-- Folder picker -->
    <div class="fp-tb-item" style="position:relative;">
      <button class="fp-tb-btn" @click.stop="toggleFolderMenu">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
        </svg>
        {{ folderName(folder) || 'Folder' }}
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
          <path d="m6 9 6 6 6-6"/>
        </svg>
      </button>
      <div v-if="folderOpen" class="fp-tb-menu" @click.stop>
        <button class="fp-tb-row" @click="pickFolder(null)">All folders</button>
        <div v-if="!folders.length" class="fp-tb-empty">No folders</div>
        <button v-for="f in folders" :key="folderName(f) || f"
                class="fp-tb-row"
                :class="{ active: folderName(folder) === folderName(f) }"
                @click="pickFolder(f)">{{ folderName(f) }}</button>
      </div>
    </div>

    <!-- Search -->
    <div class="fp-tb-search">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2" stroke-linecap="round">
        <circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>
      </svg>
      <input :value="search" placeholder="Search" @input="onSearchInput" />
    </div>

    <!-- Filter (icon-only) -->
    <div class="fp-tb-item" style="position:relative;">
      <button class="fp-tb-btn fp-tb-btn--icon"
              title="Filter"
              @click.stop="toggleFilterMenu">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
        </svg>
        <span v-if="filterCount" class="fp-tb-badge">{{ filterCount }}</span>
      </button>
      <div v-if="filterOpen" class="fp-tb-menu fp-tb-menu--filter" @click.stop>
        <div class="fp-tb-section">Type</div>
        <button v-for="t in TYPE_OPTIONS" :key="t.value" class="fp-tb-row"
                @click="$emit('toggle-type', t.value)">
          <span class="fp-tb-check" :class="{ checked: activeTypes.includes(t.value) }">
            <svg v-if="activeTypes.includes(t.value)" width="8" height="8" viewBox="0 0 12 12">
              <polyline points="2 6 5 9 10 3" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round"/>
            </svg>
          </span>
          {{ t.label }}
        </button>
        <div class="fp-tb-sep"></div>
        <button class="fp-tb-clear" @click="$emit('clear-filters')">Clear filters</button>
      </div>
    </div>

    <!-- Sort (icon-only) — small dot when non-default -->
    <div class="fp-tb-item" style="position:relative;">
      <button class="fp-tb-btn fp-tb-btn--icon"
              title="Sort"
              @click.stop="toggleSortMenu">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M7 3v18M3 7l4-4 4 4M17 21V3M13 17l4 4 4-4"/>
        </svg>
        <span v-if="sort !== 'newest'" class="fp-tb-dot"></span>
      </button>
      <div v-if="sortOpen" class="fp-tb-menu fp-tb-menu--sort" @click.stop>
        <div class="fp-tb-section">Sort by</div>
        <button v-for="opt in SORT_OPTIONS" :key="opt.id" class="fp-tb-row"
                :class="{ active: sort === opt.id }"
                @click="pickSort(opt.id)">
          <span class="fp-tb-check fp-tb-check--radio" :class="{ checked: sort === opt.id }">
            <svg v-if="sort === opt.id" width="8" height="8" viewBox="0 0 12 12">
              <circle cx="6" cy="6" r="3" fill="white"/>
            </svg>
          </span>
          {{ opt.label }}
        </button>
      </div>
    </div>

    <!-- Refresh — re-fetch products from Fangate. Fangate doesn't push,
         so this is how the user pulls fresh state without changing tabs. -->
    <button class="fp-tb-refresh"
            :disabled="isLoadingProducts"
            @click.stop="$emit('refresh')"
            :title="isLoadingProducts ? 'Refreshing…' : 'Refresh from Fangate'">
      <svg class="fp-tb-refresh-icon" :class="{ spinning: isLoadingProducts }"
           width="13" height="13" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="23 4 23 10 17 10"/>
        <polyline points="1 20 1 14 7 14"/>
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
      </svg>
    </button>
  </div>
</template>

<style lang="scss" scoped>
// ===== Toolbar =====
.fp-toolbar {
  padding: 8px 12px;
  border-bottom: 1px solid var(--border-soft);
  display: flex; align-items: center; gap: 6px;
  background: var(--bg-panel); flex-shrink: 0;
}
.fp-tb-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 9px; border-radius: 7px;
  border: 1px solid var(--border-soft);
  background: var(--bg-subtle); color: var(--ink-medium);
  font-family: 'Inter', sans-serif;
  font-size: 11px; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  &:hover { border-color: rgba(31, 42, 42, 0.4); color: var(--ink-strong); }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid var(--accent-ring); outline-offset: 2px; }
  // Icon-only variant — square, no padding, used for Filter + Sort
  // when the panel is too narrow for full text labels.
  &--icon {
    width: 28px; height: 28px;
    padding: 0; gap: 0;
    justify-content: center;
    position: relative;
    flex-shrink: 0;
  }
}
.fp-tb-badge {
  background: rgba(31, 42, 42, 0.85); color: #f5efe0;
  border-radius: 999px;
  font-size: 9px; font-weight: 700; padding: 1px 5px;
  // In icon-only mode, badge becomes a corner indicator instead of inline.
  .fp-tb-btn--icon & {
    position: absolute;
    top: -5px; right: -5px;
    min-width: 14px; line-height: 1.2;
    padding: 1px 4px;
    border: 1.5px solid var(--bg-panel);
    box-sizing: border-box;
  }
}
// Small purple dot — shown on the Sort icon button when the active sort
// is not the default ("newest"). Visual cue that something is customized.
.fp-tb-dot {
  position: absolute;
  top: 4px; right: 4px;
  width: 6px; height: 6px;
  border-radius: 50%;
  background: #7040e0;
  box-shadow: 0 0 0 2px var(--bg-subtle);
}
.fp-tb-search {
  flex: 1; position: relative; min-width: 0;
  svg {
    position: absolute; left: 8px; top: 50%; transform: translateY(-50%);
    color: var(--ink-muted); pointer-events: none;
  }
  input {
    width: 100%; box-sizing: border-box;
    font-family: 'Inter', sans-serif;
    font-size: 11.5px; padding: 5px 8px 5px 26px;
    border-radius: 7px; border: 1px solid var(--border-soft);
    background: var(--bg-subtle); outline: none; color: var(--ink-strong);
    &:focus {
      border-color: rgba(31, 42, 42, 0.4);
      box-shadow: 0 0 0 2px rgba(31, 42, 42, 0.08);
    }
  }
}
.fp-tb-menu {
  position: absolute; top: calc(100% + 4px); left: 0;
  min-width: 160px;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 9px;
  box-shadow: 0 6px 20px rgba(11, 20, 20, 0.12);
  padding: 5px; z-index: 100;
  &--filter { left: auto; right: 0; min-width: 180px; }
  // Sort menu sits second-from-right (next to refresh button); align right
  // so it doesn't overflow the panel edge.
  &--sort   { left: auto; right: 0; min-width: 170px; }
}
.fp-tb-row {
  display: flex; align-items: center; gap: 7px;
  width: 100%; padding: 6px 8px;
  border-radius: 6px; border: none; background: transparent;
  color: var(--ink-strong);
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 500;
  cursor: pointer; text-align: left;
  &:hover { background: var(--bg-subtle); }
  &.active { background: var(--accent-tint); color: var(--accent-strong); }
}
.fp-tb-section {
  font-family: 'Inter', sans-serif;
  font-size: 9px; font-weight: 700;
  letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--ink-muted); padding: 5px 8px 4px;
}
.fp-tb-sep { height: 1px; background: var(--border-soft); margin: 4px 6px; }
.fp-tb-empty {
  font-family: 'Inter', sans-serif;
  font-size: 11px; color: var(--ink-muted);
  padding: 6px 8px; font-style: italic;
}
.fp-tb-clear {
  display: flex; justify-content: center; width: 100%;
  padding: 6px 8px; border-radius: 6px;
  border: none; background: transparent;
  color: #b91c1c;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 600;
  cursor: pointer;
  &:hover { background: #fef2f2; }
}
.fp-tb-check {
  width: 14px; height: 14px; border-radius: 3px;
  border: 1.5px solid var(--border-soft);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  &.checked { border-color: var(--accent); background: var(--accent); }
  // Radio variant — used by the Sort menu (exclusive selection).
  &--radio { border-radius: 50%; }
}

// ===== Refresh button =====
.fp-tb-refresh {
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px;
  padding: 0; border-radius: 7px;
  border: 1px solid var(--border-soft);
  background: var(--bg-subtle); color: var(--ink-medium);
  cursor: pointer;
  transition: border-color 0.12s, color 0.12s, background 0.12s;
  flex-shrink: 0;
  &:hover:not(:disabled) {
    border-color: rgba(31, 42, 42, 0.4);
    color: var(--ink-strong);
    background: var(--bg-panel);
  }
  &:disabled { opacity: 0.6; cursor: wait; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid var(--accent-ring); outline-offset: 2px; }
}
.fp-tb-refresh-icon {
  transition: transform 0.2s;
  &.spinning { animation: fp-spin 0.9s linear infinite; }
}
@keyframes fp-spin {
  to { transform: rotate(360deg); }
}
</style>
